﻿import type { GridValidRowModel, GridValueFormatter, GridValueGetter } from "@mui/x-data-grid";
import { csvEscape } from "@/utils/csv";
import { downloadBlob } from "@/utils/downloadBlob";
import type { SystemGridColDef } from "./types";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const CSV_MIME = "text/csv;charset=utf-8";

export type ExportFormat = "xlsx" | "csv";

/** Every format the toolbar can offer, in menu order. */
export const EXPORT_FORMATS: readonly ExportFormat[] = ["xlsx", "csv"];

// Excel column widths, in characters. The padding leaves room for the header's filter arrow; the cap
// keeps one long free-text cell from pushing every other column off screen.
const XLSX_MIN_COLUMN_WIDTH = 10;
const XLSX_MAX_COLUMN_WIDTH = 60;
const XLSX_COLUMN_PADDING = 2;

/** A flat, export-ready view of the grid: header labels + a value matrix (one inner array per row). */
export interface ExportTable {
	headers: string[];
	matrix: (string | number)[][];
}

/**
 * Resolve one cell's exported value, mirroring MUI's own value pipeline so the export matches what's
 * on screen. Priority: an explicit `getExportValue` (the escape hatch for computed / `renderCell`-only
 * columns), then `valueGetter` → `valueFormatter` (the same path MUI uses to render a text cell), then
 * the raw field value. Null/undefined collapse to "".
 */
function resolveExportCell<T extends GridValidRowModel>(col: SystemGridColDef<T>, row: T): string | number {
	if (col.getExportValue) {
		const v = col.getExportValue(row);
		return v == null ? "" : v;
	}
	const rawField = (row as Record<string, unknown>)[col.field];
	// MUI's runtime never invokes these with a real apiRef during export; the {} matches the existing
	// page-level `downloadCsv` cast and is unused by formatters that only read value/row.
	const got = col.valueGetter ? (col.valueGetter as unknown as GridValueGetter)(rawField as never, row as never, col as never, {} as never) : rawField;
	const formatted = col.valueFormatter ? (col.valueFormatter as unknown as GridValueFormatter)(got as never, row as never, col as never, {} as never) : got;
	if (formatted == null) return "";
	return typeof formatted === "number" ? formatted : String(formatted);
}

/**
 * Build the header + value matrix once, shared by both formats. Columns flagged `disableExport` are
 * dropped. Exported (not just for the format helpers) so the value-resolution rules can be unit-tested
 * without touching the DOM/download path.
 */
export function buildExportTable<T extends GridValidRowModel>(rows: readonly T[], columns: readonly SystemGridColDef<T>[]): ExportTable {
	const exportCols = columns.filter((c) => !c.disableExport);
	const headers = exportCols.map((c) => c.headerName ?? String(c.field));
	const matrix = rows.map((row) => exportCols.map((c) => resolveExportCell(c, row)));
	return { headers, matrix };
}

/** Build a CSV from the displayed rows and trigger a download. */
export function exportRowsAsCsv<T extends GridValidRowModel>(rows: readonly T[], columns: readonly SystemGridColDef<T>[], fileName: string): void {
	const { headers, matrix } = buildExportTable(rows, columns);
	const lines = [headers, ...matrix].map((cells) => cells.map(csvEscape).join(","));
	// Prepend a UTF-8 BOM so Excel opens non-ASCII (accents, Hebrew) correctly.
	const encoded = new TextEncoder().encode(`﻿${lines.join("\n")}`);
	downloadBlob(encoded.buffer as ArrayBuffer, `${fileName}.csv`, CSV_MIME);
}

/**
 * Build an XLSX from the displayed rows and trigger a download. ExcelJS is imported dynamically so the
 * (heavy) library is only pulled into the bundle for grids that actually export.
 */
export async function exportRowsAsXlsx<T extends GridValidRowModel>(
	rows: readonly T[],
	columns: readonly SystemGridColDef<T>[],
	fileName: string,
): Promise<void> {
	await exportSheetsAsXlsx([{ name: "Sheet1", table: buildExportTable(rows, columns) }], fileName);
}

/**
 * Width (in characters) for each column of the sheet: wide enough for its header and its longest
 * value, so titles aren't cut off when the file opens. Excel has no auto-fit on open, so it is set here.
 */
export function xlsxColumnWidths({ headers, matrix }: ExportTable): number[] {
	return headers.map((header, i) => {
		const longest = matrix.reduce((max, cells) => Math.max(max, String(cells[i]).length), header.length);
		return Math.min(XLSX_MAX_COLUMN_WIDTH, Math.max(XLSX_MIN_COLUMN_WIDTH, longest + XLSX_COLUMN_PADDING));
	});
}

/** One worksheet of a multi-sheet export; `name` is already translated. */
export type ExportSheet = { name: string; table: ExportTable };

/**
 * Several tables in one XLSX, a worksheet each — for a report whose detail rows are a list of their own (a shift
 * list plus every request under it). Build each table with `buildExportTable` so the cells match the grid.
 */
export async function exportSheetsAsXlsx(sheets: readonly ExportSheet[], fileName: string): Promise<void> {
	const { Workbook } = await import("exceljs");
	const workbook = new Workbook();
	for (const { name, table } of sheets) {
		const sheet = workbook.addWorksheet(name);
		sheet.addRow(table.headers);
		for (const cells of table.matrix) sheet.addRow(cells);
		xlsxColumnWidths(table).forEach((width, i) => {
			sheet.getColumn(i + 1).width = width;
		});
	}
	const buffer = await workbook.xlsx.writeBuffer();
	downloadBlob(buffer, `${fileName}.xlsx`, XLSX_MIME);
}
